import { ReactElement, useState } from 'react';

import Button from '~/ui/components/common/Button';
import Modal from '~/ui/components/common/Modal';
import CrisisListItemDetails from '~/ui/pages/Network/components/Crisis/CrisisListItemDetails';
import formatClientAddress from '~/utils/dataFormat/formatClientAddress';

import { useApiRequest } from '~/hooks/api';
import useTabVisibility from '~/hooks/browser/useTabVisibility';
import { ICrisisCall } from '~/services/api/crisis/types';
import { ALLOCATIONS_FILTER } from '~/store/crisis/types';
import { useStoreActions, useStoreState } from '~/store/hooks';
import useCrisisSignalR from '~/ui/hooks/useCrisisSignalR';

import styles from './SignalRCrisis.module.scss';

const SignalRCrisisAlert = (): ReactElement => {
  const [crisisCall, setCrisisCall] = useState<ICrisisCall | null>(null);
  const user = useStoreState(state => state.user.current);
  const clinicId = user?.clinic?.id;
  const crisisAccess = !!user?.crisisAccess;
  const { allocationsFilter } = useStoreState(state => state.crisis);

  const { onGetCrisisCalls, onGetCrisisCallTeamMembers } = useStoreActions(
    actions => actions.crisis,
  );

  const { refetch: refetchCrisisCalls } = useApiRequest(
    () => onGetCrisisCalls({ clinicId: String(clinicId) }),
    {
      enabled: false,
    },
  );

  const { refetch: refetchGetCrisisCallTeamMembers } = useApiRequest(
    () =>
      onGetCrisisCallTeamMembers({
        clinicId: String(clinicId),
        onlyCrisis: allocationsFilter === ALLOCATIONS_FILTER.CRISIS_ONLY,
      }),
    {
      enabled: false,
    },
  );

  const refetchCrisisCallData = () => {
    refetchCrisisCalls();
    refetchGetCrisisCallTeamMembers();
  };

  const onCrisisCallReceived = (data: ICrisisCall) => {
    setCrisisCall(data);
    refetchCrisisCallData();
  };

  useCrisisSignalR<ICrisisCall>({ action: 'CrisisCallReceived', callback: onCrisisCallReceived });
  useCrisisSignalR({ action: 'CrisisQueueUpdated', callback: refetchCrisisCallData });
  useCrisisSignalR({
    action: 'CrisisStaffAllocationsUpdated',
    callback: refetchCrisisCallData,
  });

  useTabVisibility(clinicId && crisisAccess ? refetchCrisisCallData : undefined);

  const resetCrisisCall = () => {
    setCrisisCall(null);
  };

  if (!crisisCall) {
    return null;
  }

  return (
    <Modal
      open={!!crisisCall}
      className={styles.modal}
      onClose={resetCrisisCall}
      maxWidth="sm"
      fullWidth
    >
      <>
        <div className={styles.modalTitle}>New Crisis Client</div>
        <div className={styles.modalWrapper}>
          <CrisisListItemDetails
            photo={crisisCall.client.photo}
            name={`${crisisCall.client.firstName} ${crisisCall.client.lastName}`}
            emrMrn={crisisCall.client.emrMrn}
            location={formatClientAddress(crisisCall.address)}
            timeCall={crisisCall.receivedOn}
            coordinates={{ latitude: crisisCall.address?.lat, longitude: crisisCall.address?.lng }}
          />
        </div>
        <div className={styles.actionButtons}>
          <Button color="primary" variant="contained" onClick={resetCrisisCall}>
            Close
          </Button>
        </div>
      </>
    </Modal>
  );
};

export default SignalRCrisisAlert;
